import { useEffect, useRef, useState } from "react";
import type { Observatory } from "./schema";
import type { ReportingPeriod } from "../../communityReportingApi";

export type PeriodLoader = (
  from: string,
  to: string,
  signal: AbortSignal,
) => Promise<ReportingPeriod>;
export type AppliedPeriod = { from: string; to: string };
export function useReportingPeriod(
  base: Observatory | null | undefined,
  loader?: PeriodLoader | undefined,
) {
  const [applied, setApplied] = useState<AppliedPeriod | null>(null);
  const [result, setResult] = useState<Observatory | null>(null);
  const [phase, setPhase] = useState<"idle" | "loading" | "error">("idle");
  const [resetKey, setResetKey] = useState(0);
  const pending = useRef<AbortController | null>(null);
  function clear() {
    pending.current?.abort();
    pending.current = null;
    setApplied(null);
    setResult(null);
    setPhase("idle");
    setResetKey((value) => value + 1);
  }
  useEffect(() => {
    clear();
    return () => pending.current?.abort();
  }, [base]);
  async function apply(from: string, to: string) {
    if (!loader || !base) return;
    pending.current?.abort();
    const controller = new AbortController();
    pending.current = controller;
    setApplied({ from, to });
    setResult(null);
    setPhase("loading");
    try {
      const next = await loader(from, to, controller.signal);
      if (controller.signal.aborted || pending.current !== controller) return;
      if (
        next.date_filter.from_date !== from ||
        next.date_filter.to_date !== to
      )
        throw new Error("Reporting period response does not match the request");
      setResult({
        ...next.observatory,
        countries: base.countries,
        provinces: base.provinces,
      });
      setPhase("idle");
    } catch {
      if (!controller.signal.aborted && pending.current === controller)
        setPhase("error");
    }
  }
  return {
    data: applied ? result : base,
    applied,
    phase,
    apply,
    clear,
    resetKey,
  };
}
